import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Shield, 
  Smartphone, 
  Key, 
  Eye, 
  EyeOff, 
  Lock, 
  LogOut, 
  Monitor, 
  History, 
  Download,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Copy,
  RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SecuritySettings = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [twoFactorStatus, setTwoFactorStatus] = useState({ enabled: false });
  const [sessions, setSessions] = useState([]);
  const [loginHistory, setLoginHistory] = useState([]);
  const [passwordPolicy, setPasswordPolicy] = useState({});
  
  // 2FA Setup
  const [showSetup2FA, setShowSetup2FA] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodesCopied, setBackupCodesCopied] = useState(false);
  
  // Password Change
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPwd, setShowCurrentPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ valid: false, message: '' });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const fetchSecurityData = async () => {
    setLoading(true);
    try {
      const [statusRes, sessionsRes, historyRes, policyRes] = await Promise.all([
        axios.get(`${API}/api/security/2fa/status`, { headers }),
        axios.get(`${API}/api/security/sessions`, { headers }),
        axios.get(`${API}/api/security/login-history`, { headers }),
        axios.get(`${API}/api/security/password-policy`)
      ]);
      
      setTwoFactorStatus(statusRes.data);
      setSessions(sessionsRes.data);
      setLoginHistory(historyRes.data);
      setPasswordPolicy(policyRes.data);
    } catch (error) {
      console.error('Error fetching security data:', error);
      toast.error('Failed to load security settings');
    } finally {
      setLoading(false);
    }
  };

  // ==================== 2FA Functions ====================
  
  const initiate2FASetup = async () => {
    try {
      const res = await axios.post(`${API}/api/security/2fa/setup`, {}, { headers });
      setSetupData(res.data);
      setShowSetup2FA(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate 2FA setup');
    }
  };

  const verify2FASetup = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    try {
      await axios.post(`${API}/api/security/2fa/verify`, { code: verificationCode }, { headers });
      toast.success('2FA enabled successfully!');
      setShowSetup2FA(false);
      setSetupData(null);
      setVerificationCode('');
      fetchSecurityData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    }
  };

  const copyBackupCodes = () => {
    if (setupData?.backup_codes) {
      navigator.clipboard.writeText(setupData.backup_codes.join('\n'));
      setBackupCodesCopied(true);
      toast.success('Backup codes copied to clipboard');
    }
  };

  // ==================== Password Functions ====================
  
  const validatePassword = async (password) => {
    if (!password) {
      setPasswordStrength({ valid: false, message: '' });
      return;
    }
    
    // Client-side validation based on policy
    const checks = [];
    if (password.length < passwordPolicy.min_length) {
      checks.push(`At least ${passwordPolicy.min_length} characters`);
    }
    if (passwordPolicy.require_uppercase && !/[A-Z]/.test(password)) {
      checks.push('One uppercase letter');
    }
    if (passwordPolicy.require_lowercase && !/[a-z]/.test(password)) {
      checks.push('One lowercase letter');
    }
    if (passwordPolicy.require_number && !/[0-9]/.test(password)) {
      checks.push('One number');
    }
    if (passwordPolicy.require_special && !/[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password)) {
      checks.push('One special character');
    }
    
    if (checks.length > 0) {
      setPasswordStrength({ valid: false, message: `Missing: ${checks.join(', ')}` });
    } else {
      setPasswordStrength({ valid: true, message: 'Password meets requirements' });
    }
  };

  const changePassword = async () => {
    if (!passwordStrength.valid) {
      toast.error('Password does not meet requirements');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    try {
      await axios.post(`${API}/api/security/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, { headers });
      
      toast.success('Password changed successfully. Please log in again.');
      setShowPasswordChange(false);
      // Logout user after password change
      setTimeout(() => window.location.href = '/login', 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  // ==================== Session Functions ====================
  
  const revokeSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/api/security/sessions/${sessionId}`, { headers });
      toast.success('Session revoked');
      fetchSecurityData();
    } catch (error) {
      toast.error('Failed to revoke session');
    }
  };

  const revokeAllSessions = async () => {
    try {
      await axios.delete(`${API}/api/security/sessions`, { headers });
      toast.success('All sessions revoked. Logging out...');
      setTimeout(() => window.location.href = '/login', 2000);
    } catch (error) {
      toast.error('Failed to revoke sessions');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 p-4 md:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Security Settings</h1>
            <p className="text-slate-500">Manage your account security and privacy</p>
          </div>
        </div>

        <Tabs defaultValue="authentication" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-grid">
            <TabsTrigger value="authentication" className="gap-2">
              <Lock className="w-4 h-4" />
              <span className="hidden sm:inline">Authentication</span>
            </TabsTrigger>
            <TabsTrigger value="sessions" className="gap-2">
              <Monitor className="w-4 h-4" />
              <span className="hidden sm:inline">Sessions</span>
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">History</span>
            </TabsTrigger>
          </TabsList>

          {/* Authentication Tab */}
          <TabsContent value="authentication" className="space-y-6">
            {/* Two-Factor Authentication */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-indigo-600" />
                  Two-Factor Authentication (2FA)
                </CardTitle>
                <CardDescription>
                  Add an extra layer of security to your account using an authenticator app
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {twoFactorStatus.enabled ? (
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    ) : (
                      <XCircle className="w-6 h-6 text-slate-400" />
                    )}
                    <div>
                      <p className="font-medium">
                        2FA is {twoFactorStatus.enabled ? 'Enabled' : 'Disabled'}
                      </p>
                      {twoFactorStatus.enabled && (
                        <p className="text-sm text-slate-500">
                          {twoFactorStatus.backup_codes_remaining} backup codes remaining
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {!twoFactorStatus.enabled ? (
                    <Button onClick={initiate2FASetup} className="bg-indigo-600 hover:bg-indigo-700">
                      Enable 2FA
                    </Button>
                  ) : (
                    <Badge className="bg-green-100 text-green-700">Protected</Badge>
                  )}
                </div>
                
                {!twoFactorStatus.enabled && (
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex gap-2">
                      <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
                      <div>
                        <p className="font-medium text-amber-800">Recommended for admins</p>
                        <p className="text-sm text-amber-700">
                          Enable 2FA to protect your account from unauthorized access
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Password */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-indigo-600" />
                  Password
                </CardTitle>
                <CardDescription>
                  Keep your account secure with a strong password
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Password Requirements</p>
                    <ul className="text-sm text-slate-500 mt-1 space-y-0.5">
                      <li>• Minimum {passwordPolicy.min_length} characters</li>
                      {passwordPolicy.require_uppercase && <li>• At least one uppercase letter</li>}
                      {passwordPolicy.require_number && <li>• At least one number</li>}
                      {passwordPolicy.require_special && <li>• At least one special character</li>}
                    </ul>
                  </div>
                  <Dialog open={showPasswordChange} onOpenChange={setShowPasswordChange}>
                    <DialogTrigger asChild>
                      <Button variant="outline">Change Password</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Change Password</DialogTitle>
                        <DialogDescription>
                          Enter your current password and choose a new one
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Current Password</Label>
                          <div className="relative">
                            <Input
                              type={showCurrentPwd ? 'text' : 'password'}
                              value={currentPassword}
                              onChange={(e) => setCurrentPassword(e.target.value)}
                              placeholder="Enter current password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowCurrentPwd(!showCurrentPwd)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                            >
                              {showCurrentPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>New Password</Label>
                          <div className="relative">
                            <Input
                              type={showNewPwd ? 'text' : 'password'}
                              value={newPassword}
                              onChange={(e) => {
                                setNewPassword(e.target.value);
                                validatePassword(e.target.value);
                              }}
                              placeholder="Enter new password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPwd(!showNewPwd)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                            >
                              {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                          {passwordStrength.message && (
                            <p className={`text-sm ${passwordStrength.valid ? 'text-green-600' : 'text-red-600'}`}>
                              {passwordStrength.message}
                            </p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label>Confirm New Password</Label>
                          <Input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                          />
                          {confirmPassword && newPassword !== confirmPassword && (
                            <p className="text-sm text-red-600">Passwords do not match</p>
                          )}
                        </div>
                        <Button 
                          onClick={changePassword} 
                          className="w-full bg-indigo-600 hover:bg-indigo-700"
                          disabled={!passwordStrength.valid || newPassword !== confirmPassword}
                        >
                          Change Password
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sessions Tab */}
          <TabsContent value="sessions" className="space-y-6">
            <Card className="border-0 shadow-lg">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Monitor className="w-5 h-5 text-indigo-600" />
                    Active Sessions
                  </CardTitle>
                  <CardDescription>
                    Manage devices where you're currently logged in
                  </CardDescription>
                </div>
                <Button 
                  variant="outline" 
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  onClick={revokeAllSessions}
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout All
                </Button>
              </CardHeader>
              <CardContent>
                {sessions.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No active sessions found</p>
                ) : (
                  <div className="space-y-3">
                    {sessions.map((session) => (
                      <div 
                        key={session.id} 
                        className={`flex items-center justify-between p-4 rounded-lg ${
                          session.is_current ? 'bg-indigo-50 border border-indigo-200' : 'bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Monitor className={`w-5 h-5 ${session.is_current ? 'text-indigo-600' : 'text-slate-400'}`} />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{session.device}</p>
                              {session.is_current && (
                                <Badge className="bg-indigo-100 text-indigo-700">Current</Badge>
                              )}
                            </div>
                            <p className="text-sm text-slate-500">
                              {session.ip_address} • Last active: {formatDateTime(session.last_active)}
                            </p>
                          </div>
                        </div>
                        {!session.is_current && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => revokeSession(session.id)}
                          >
                            <LogOut className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-6">
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5 text-indigo-600" />
                  Login History
                </CardTitle>
                <CardDescription>
                  Recent login attempts to your account
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loginHistory.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">No login history available</p>
                ) : (
                  <div className="space-y-2">
                    {loginHistory.map((attempt, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center justify-between p-3 rounded-lg ${
                          attempt.success ? 'bg-green-50' : 'bg-red-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {attempt.success ? (
                            <CheckCircle className="w-5 h-5 text-green-600" />
                          ) : (
                            <XCircle className="w-5 h-5 text-red-600" />
                          )}
                          <div>
                            <p className="font-medium">
                              {attempt.success ? 'Successful login' : 'Failed attempt'}
                            </p>
                            <p className="text-sm text-slate-500">
                              {attempt.ip_address || 'Unknown IP'}
                            </p>
                          </div>
                        </div>
                        <p className="text-sm text-slate-500">
                          {formatDateTime(attempt.timestamp)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* 2FA Setup Dialog */}
        <Dialog open={showSetup2FA} onOpenChange={setShowSetup2FA}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
              <DialogDescription>
                Scan the QR code with your authenticator app
              </DialogDescription>
            </DialogHeader>
            {setupData && (
              <div className="space-y-4">
                {/* Step 1: QR Code */}
                <div className="text-center">
                  <div className="inline-block p-2 bg-white rounded-lg border">
                    <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48" />
                  </div>
                  <p className="text-sm text-slate-500 mt-2">
                    Or enter manually: <code className="bg-slate-100 px-2 py-1 rounded text-xs">{setupData.secret}</code>
                  </p>
                </div>

                {/* Step 2: Backup Codes */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="font-medium text-amber-800">Backup Codes</p>
                    <Button size="sm" variant="ghost" onClick={copyBackupCodes}>
                      <Copy className="w-4 h-4 mr-1" />
                      {backupCodesCopied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {setupData.backup_codes.map((code, idx) => (
                      <code key={idx} className="text-sm bg-white px-2 py-1 rounded border text-center">
                        {code}
                      </code>
                    ))}
                  </div>
                  <p className="text-xs text-amber-700 mt-2">
                    Save these codes securely. Each can only be used once.
                  </p>
                </div>

                {/* Step 3: Verify */}
                <div className="space-y-2">
                  <Label>Enter code from your app</Label>
                  <Input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest"
                    maxLength={6}
                  />
                </div>

                <Button 
                  onClick={verify2FASetup} 
                  className="w-full bg-indigo-600 hover:bg-indigo-700"
                  disabled={verificationCode.length !== 6}
                >
                  Verify & Enable 2FA
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SecuritySettings;
